import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ROUTE_COMPLETE_PROFILE, ROUTE_JOBS_DASHBOARD, ROUTE_MAIN } from "../../constants/routes";
import {
  ApiNotification,
  fetchNotificationsList,
  getNotificationNavigationTarget,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  navigateToNotificationTarget,
} from "../../utils/notificationsApi";
import { FCM_FOREGROUND_EVENT } from "../../utils/fcmForegroundEvents";
import { logoutClear } from "../../redux/actions/appActions";
import NotificationNavButton from "../common/NotificationNavButton";
import NotificationCard from "./NotificationCard";
import {
  BG_CARD,
  BG_PAGE,
  BG_SUBTLE,
  BORDER_LIGHT,
  FONT_FAMILY,
  ORANGE_HEADER,
  PRIMARY,
  PRIMARY_DARK,
  PRIMARY_LIGHT,
  PRIMARY_BORDER,
  RADIUS_BUTTON,
  RADIUS_CARD,
  RADIUS_PILL,
  TEXT_MUTED,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from "./notificationTheme";

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "20px",
  cursor: "pointer",
}));

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

const PAGE_SIZE = 10;

const pageFont = { fontFamily: FONT_FAMILY };

const getPushDisabledMessage = (permission: NotificationPermission): string => {
  if (permission === "denied") {
    return "Push notifications are blocked for SeeVi. Open your browser's site settings and allow notifications for this site to receive job and application updates.";
  }
  return "To receive push notifications, allow notifications when your browser asks, or enable them in your browser's site settings.";
};

const NotificationsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [items, setItems] = useState<ApiNotification[]>([]);
  const [apiUnread, setApiUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const loadPage = useCallback(async (pageNum: number, reset: boolean, onlyUnread: boolean) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);

    try {
      const result = await fetchNotificationsList(pageNum, PAGE_SIZE, onlyUnread);
      setApiUnread(result.unreadCount);
      setHasMore(result.hasNext);
      setPage(result.page);
      setItems((prev) => (reset ? result.records : [...prev, ...result.records]));
    } catch (err) {
      console.warn("[notifications] Failed to load notifications:", err);
      if (reset) setItems([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1, true, unreadOnly);
  }, [unreadOnly, loadPage]);

  useEffect(() => {
    const onFcm = () => loadPage(1, true, unreadOnly);
    window.addEventListener(FCM_FOREGROUND_EVENT, onFcm);
    return () => window.removeEventListener(FCM_FOREGROUND_EVENT, onFcm);
  }, [loadPage, unreadOnly]);

  useEffect(() => {
    if (typeof Notification !== "undefined") {
      setPermission(Notification.permission);
    }
  }, []);

  const handleBack = () => {
    const roles = localStorage.getItem("roles");
    if (roles && roles.length > 0 && !roles.includes("job_seeker")) {
      navigate(ROUTE_JOBS_DASHBOARD);
    } else {
      navigate(ROUTE_COMPLETE_PROFILE);
    }
  };

  const handleLogout = () => {
    dispatch(logoutClear(() => navigate(ROUTE_MAIN, { replace: true })));
  };

  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    try {
      await markAllNotificationsAsRead();
      setApiUnread(0);
      await loadPage(1, true, unreadOnly);
    } catch (err) {
      console.warn("[notifications] Failed to mark all as read:", err);
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleItemClick = async (item: ApiNotification) => {
    const wasUnread = !item.read;

    if (wasUnread) {
      setItems((prev) =>
        unreadOnly
          ? prev.filter((n) => n.id !== item.id)
          : prev.map((n) =>
              n.id === item.id
                ? { ...n, read: true, readAt: n.readAt ?? new Date().toISOString() }
                : n
            )
      );
      setApiUnread((c) => Math.max(0, c - 1));

      try {
        await markNotificationAsRead(item.id);
      } catch (err) {
        console.warn("[notifications] Failed to mark notification as read:", err);
        await loadPage(1, true, unreadOnly);
      }
    }

    const target = getNotificationNavigationTarget(item);
    if (target) {
      navigateToNotificationTarget(navigate, target);
    }
  };

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    loadPage(page + 1, false, unreadOnly);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: BG_PAGE, ...pageFont }}>
      {/* App header — matches SkillSetNew / ExperienceNew */}
      <Box
        sx={{
          display: "flex",
          backgroundColor: ORANGE_HEADER,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Header onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}>
          <Logo src="/images/client-website/seevi-logo.png" alt="SeeVi" />
        </Header>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <NotificationNavButton iconColor="#fff" />
          <Box sx={{ mr: 1 }} onClick={() => navigate(ROUTE_COMPLETE_PROFILE)} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
              <HomeIcon />
            </InputAdornment>
          </Box>
          <Box sx={{ mr: 2 }} onClick={handleLogout} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
              <LogoutIcon />
            </InputAdornment>
          </Box>
        </Box>
      </Box>

      {/* Page title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 1.25,
          borderBottom: `1px solid ${BORDER_LIGHT}`,
          bgcolor: BG_CARD,
        }}
      >
        <IconButton onClick={handleBack} aria-label="Go back" sx={{ color: TEXT_PRIMARY }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 600, fontSize: 18, color: TEXT_PRIMARY, flex: 1 }}>
          Notifications
        </Typography>
        <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mr: 1 }}>
          {apiUnread > 0 ? `${apiUnread} new` : null}
          {apiUnread > 0 && items.length > 0 ? " · " : null}
          {items.length > 0 ? `${items.length} shown` : null}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 640, mx: "auto", px: 2, py: 2, pb: 4 }}>
        {/* Filters */}
        <Box
          sx={{
            bgcolor: BG_CARD,
            borderRadius: RADIUS_CARD,
            p: 1.5,
            mb: 2,
            border: `1px solid ${BORDER_LIGHT}`,
          }}
        >
          <Stack direction="row" spacing={1} sx={{ mb: apiUnread > 0 ? 1.5 : 0 }}>
            {(["all", "unread"] as const).map((filter) => {
              const active = filter === "unread" ? unreadOnly : !unreadOnly;
              return (
                <Button
                  key={filter}
                  fullWidth
                  onClick={() => setUnreadOnly(filter === "unread")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 14,
                    borderRadius: RADIUS_PILL,
                    py: 1,
                    bgcolor: active ? PRIMARY : PRIMARY_LIGHT,
                    color: active ? "#fff" : PRIMARY,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: active ? PRIMARY_DARK : "rgba(117, 109, 233, 0.18)",
                    },
                  }}
                >
                  {filter === "all" ? "All" : `New${apiUnread > 0 ? ` (${apiUnread})` : ""}`}
                </Button>
              );
            })}
          </Stack>

          {apiUnread > 0 ? (
            <Button
              fullWidth
              disabled={markingAllRead}
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllRead}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: 14,
                borderRadius: RADIUS_BUTTON,
                color: PRIMARY,
                border: `1px dashed ${PRIMARY_BORDER}`,
                py: 1,
                "&:hover": { bgcolor: PRIMARY_LIGHT },
              }}
            >
              {markingAllRead ? "Updating…" : "Mark all as read"}
            </Button>
          ) : null}
        </Box>

        {permission !== "granted" ? (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: RADIUS_CARD,
              bgcolor: BG_SUBTLE,
              border: `1px solid ${BORDER_LIGHT}`,
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: RADIUS_BUTTON,
                  bgcolor: PRIMARY_LIGHT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <NotificationsActiveOutlinedIcon sx={{ color: PRIMARY }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: TEXT_PRIMARY, mb: 0.5 }}>
                  Stay in the loop
                </Typography>
                <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.45 }}>
                  {getPushDisabledMessage(permission)}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
            <CircularProgress sx={{ color: PRIMARY }} size={40} />
            <Typography sx={{ color: TEXT_SECONDARY, fontSize: 14 }}>Loading notifications…</Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 3,
              borderRadius: RADIUS_CARD,
              bgcolor: BG_CARD,
              border: `1px dashed ${BORDER_LIGHT}`,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: PRIMARY_LIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <NotificationsNoneOutlinedIcon sx={{ fontSize: 36, color: PRIMARY }} />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: TEXT_PRIMARY }}>
              {unreadOnly ? "All caught up" : "No notifications yet"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mt: 0.75, maxWidth: 280, mx: "auto" }}>
              {unreadOnly
                ? "You have no new messages. Check back later for updates."
                : "Job updates, shortlists, and recommendations will show up here."}
            </Typography>
          </Box>
        ) : (
          <InfiniteScroll
            dataLength={items.length}
            next={loadMore}
            hasMore={hasMore}
            loader={
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={32} sx={{ color: PRIMARY }} />
              </Box>
            }
            endMessage={
              items.length > 0 ? (
                <Typography sx={{ textAlign: "center", color: TEXT_MUTED, fontSize: 13, py: 2 }}>
                  You&apos;re all caught up
                </Typography>
              ) : null
            }
          >
            <Box sx={{ pt: 0.5 }}>
              {items.map((item) => (
                <NotificationCard key={item.id} item={item} onClick={() => handleItemClick(item)} />
              ))}
            </Box>
          </InfiniteScroll>
        )}
      </Box>
    </Box>
  );
};

export default NotificationsPage;
